/**
 * eventCustomFieldRows — read-only rows for an event's custom fields, for the
 * view modal.
 *
 * Legacy parity (`calendar.event-view.js:467-485`): every custom field of the
 * event's organization is listed under Instructor, with the event's value or
 * "None". The V2 modal only used custom fields to hydrate the EDIT form, so
 * the view state showed nothing at all — reported as a regression
 * (owner, 2026-09-16: "custom fields are supposed to be visible in all
 * states").
 *
 * PURE. Values come from the detail DTO's `event.customFields`, an array of
 * `{ typeId | typeID | type.id, value }` keyed by the org custom-field id
 * (see `mapEventToFormState`, which reverse-maps the same way).
 *
 * @module eventCustomFieldRows
 */

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * `YYYY-MM-DD` → `Sep 16, 2026`. Anything that is not that exact shape is
 * returned untouched — better to show the raw value than to swallow it.
 */
export function formatCustomDate(value) {
	const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || '').trim());
	if (!m) return String(value ?? '');
	const month = MONTHS[Number(m[2]) - 1];
	if (!month) return String(value);
	return `${month} ${Number(m[3])}, ${m[1]}`;
}

/**
 * @param {Object|null} event  Detail event (raw or normalized — `customFields` passes through).
 * @param {Array<{id:string|number, name:string, type:string}>} orgCustomFields
 *   The org's custom-field definitions, already filtered to the event's org.
 * @returns {Array<{id:string, name:string, type:string, value:string|null, display:string}>}
 *   One row per definition, in definition order. `display` is what to render.
 */
export function buildCustomFieldRows(event, orgCustomFields) {
	if (!event || !Array.isArray(orgCustomFields) || !orgCustomFields.length) return [];

	const valueByTypeId = new Map();
	const list = Array.isArray(event.customFields) ? event.customFields : [];
	for (const cf of list) {
		const typeId = cf?.typeId ?? cf?.typeID ?? cf?.type?.id ?? null;
		if (typeId == null) continue;
		const v = cf?.value;
		valueByTypeId.set(String(typeId), v == null ? '' : String(v));
	}

	return orgCustomFields.map(def => {
		const id = String(def.id);
		const type = String(def.type || 'text').toLowerCase();
		const raw = valueByTypeId.get(id);
		const value = raw == null || raw.trim() === '' ? null : raw;
		let display = 'None';
		if (value != null) display = type === 'date' ? formatCustomDate(value) : value;
		return { id, name: def.name || `Field ${id}`, type, value, display };
	});
}

export default buildCustomFieldRows;
